import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { RedirectIfAuthed } from "@/components/RedirectIfAuthed";
import { Shell } from "@/components/Shell";
import { Button, Card, ErrorBanner, Field, Input } from "@/components/ui-kit";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const { requestPasswordReset, session, initializing } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  if (initializing || session) {
    return (
      <Shell>
        <RedirectIfAuthed />
      </Shell>
    );
  }

  const submit = async () => {
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      return setError("Enter a valid email address.");
    }
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send reset email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <div className="mx-auto max-w-md px-6 py-16">
        <Card>
          {sent ? (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Check your inbox</div>
              <h1 className="font-display text-3xl mt-1">Reset link sent</h1>
              <p className="text-sm text-muted-foreground mt-2">
                If an account exists for <span className="text-gold">{email.trim()}</span>, you’ll get an email
                with a link to choose a new password. The link expires after a short time.
              </p>
              <div className="mt-6 space-y-3">
                <Button variant="gold" className="w-full" asChild>
                  <Link to="/login">Back to sign in</Link>
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setSent(false);
                    setError(null);
                  }}
                  className="block w-full text-xs text-muted-foreground hover:text-gold"
                >
                  Try a different email
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Account</div>
              <h1 className="font-display text-3xl mt-1">Forgot password</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Enter the email on your CrediKing account. We’ll send a reset link.
              </p>
              <div className="mt-6 space-y-4">
                <ErrorBanner message={error} />
                <Field label="Email">
                  <Input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </Field>
                <Button variant="gold" loading={loading} onClick={submit} className="w-full">
                  Send reset link
                </Button>
                <p className="text-sm text-center text-muted-foreground">
                  Remembered it?{" "}
                  <Link to="/login" className="text-gold hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            </>
          )}
        </Card>
      </div>
    </Shell>
  );
}
